/**
 * CustomerDrawer
 * Right-side slide-over panel for managing a single B2B customer.
 * Tabs: Info | Portal Access | API Tokens
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, Pencil, Save, Loader2, KeyRound, Globe, Building2,
  Copy, Trash2, Plus, Eye, EyeOff, RefreshCw, ExternalLink,
} from 'lucide-react';
import {
  b2bCustomerService,
  B2BCustomer,
  B2BCustomerUpdate,
  B2BApiToken,
  B2BApiTokenCreated,
} from '../../services/B2BCustomerService';
import {
  enablePortalAccess,
  resetPortalPassword,
  disablePortalAccess,
} from '../../services/portalApi';

// ── types ─────────────────────────────────────────────────────────────────────

type Tab = 'info' | 'portal' | 'tokens';

interface Props {
  customer: B2BCustomer | null;
  orgAlias: string;
  onClose: () => void;
  onUpdated: (c: B2BCustomer) => void;
}

// ── helpers ───────────────────────────────────────────────────────────────────

const INPUT = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 transition-all';

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

// ── Info Tab ──────────────────────────────────────────────────────────────────

const InfoTab: React.FC<{ customer: B2BCustomer; onUpdated: (c: B2BCustomer) => void }> = ({
  customer, onUpdated,
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<B2BCustomerUpdate>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const startEdit = () => {
    setForm({
      name: customer.name,
      alias: customer.alias ?? '',
      tax_id: customer.tax_id ?? '',
      contact_name: customer.contact_name ?? '',
      contact_email: customer.contact_email ?? '',
      contact_phone: customer.contact_phone ?? '',
      country: customer.country ?? '',
      city: customer.city ?? '',
      address: customer.address ?? '',
      notes: customer.notes ?? '',
      delivery_days_override: (customer.delivery_days_override != null ? String(customer.delivery_days_override) : '') as any,
    });
    setEditing(true);
  };

  const save = async () => {
    if (!form.contact_email?.trim()) {
      setError(t('customerDrawer.info.emailRequired'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload: B2BCustomerUpdate = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
      );
      // delivery_days_override is edited as a string — coerce to number|null.
      const ddRaw = (form as any).delivery_days_override;
      payload.delivery_days_override = ddRaw == null || String(ddRaw).trim() === '' ? null : Number(ddRaw);
      const res = await b2bCustomerService.update(customer.id, payload);
      onUpdated(res.data);
      setEditing(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('customerDrawer.info.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const f = (key: keyof B2BCustomerUpdate) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [key]: e.target.value }));

  if (!editing) {
    return (
      <div className="space-y-5">
        <div className="flex justify-end">
          <button
            onClick={startEdit}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 hover:border-gray-400 px-3 py-1.5 rounded-lg transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" /> {t('customerDrawer.info.edit')}
          </button>
        </div>

        <Section title={t('customerDrawer.info.section.company')}>
          <Row label={t('customerDrawer.info.field.name')} value={customer.name} />
          <Row label={t('customerDrawer.info.field.alias')} value={customer.alias} />
          <Row label={t('customerDrawer.info.field.taxId')} value={customer.tax_id} />
        </Section>

        <Section title={t('customerDrawer.info.section.contact')}>
          <Row label={t('customerDrawer.info.field.contactName')} value={customer.contact_name} />
          <Row label={t('customerDrawer.info.field.email')} value={customer.contact_email} />
          <Row label={t('customerDrawer.info.field.phone')} value={customer.contact_phone} />
        </Section>

        <Section title={t('customerDrawer.info.section.location')}>
          <Row label={t('customerDrawer.info.field.city')} value={customer.city} />
          <Row label={t('customerDrawer.info.field.country')} value={customer.country} />
          <Row label={t('customerDrawer.info.field.address')} value={customer.address} />
          <Row
            label={t('customerDrawer.info.field.deliveryOverride', 'Delivery override (days)')}
            value={customer.delivery_days_override != null ? String(customer.delivery_days_override) : null}
          />
        </Section>

        {customer.notes && (
          <Section title={t('customerDrawer.info.section.notes')}>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{customer.notes}</p>
          </Section>
        )}

        <p className="text-xs text-gray-400">
          {t('customerDrawer.info.created')} {new Date(customer.created_at).toLocaleDateString()} ·
          {t('customerDrawer.info.updated')} {new Date(customer.updated_at).toLocaleDateString()}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Field label={t('customerDrawer.info.field.companyName')}>
        <input className={INPUT} value={form.name ?? ''} onChange={f('name')} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('customerDrawer.info.field.alias')}><input className={INPUT} value={form.alias ?? ''} onChange={f('alias')} /></Field>
        <Field label={t('customerDrawer.info.field.taxId')}><input className={INPUT} value={form.tax_id ?? ''} onChange={f('tax_id')} /></Field>
      </div>
      <Field label={t('customerDrawer.info.field.contactName')}><input className={INPUT} value={form.contact_name ?? ''} onChange={f('contact_name')} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('customerDrawer.info.field.email')} required><input type="email" className={INPUT} value={form.contact_email ?? ''} onChange={f('contact_email')} /></Field>
        <Field label={t('customerDrawer.info.field.phone')}><input type="tel" className={INPUT} value={form.contact_phone ?? ''} onChange={f('contact_phone')} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('customerDrawer.info.field.city')}><input className={INPUT} value={form.city ?? ''} onChange={f('city')} /></Field>
        <Field label={t('customerDrawer.info.field.countryIso')}><input className={INPUT} maxLength={2} value={form.country ?? ''} onChange={e => setForm(p => ({ ...p, country: e.target.value.toUpperCase() }))} /></Field>
      </div>
      <Field label={t('customerDrawer.info.field.address')}><textarea className={INPUT + ' resize-none'} rows={2} value={form.address ?? ''} onChange={f('address')} /></Field>
      <Field label={t('customerDrawer.info.field.deliveryOverride', 'Delivery override (days)')}><input type="number" min={0} className={INPUT} value={(form as any).delivery_days_override ?? ''} onChange={f('delivery_days_override' as any)} /></Field>
      <Field label={t('customerDrawer.info.field.notes')}><textarea className={INPUT + ' resize-none'} rows={3} value={form.notes ?? ''} onChange={f('notes')} /></Field>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-2 pt-2">
        <button onClick={() => setEditing(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">{t('customerDrawer.info.cancel')}</button>
        <button
          onClick={save}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t('customerDrawer.info.save')}
        </button>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{title}</p>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const Row: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => (
  <div className="flex items-baseline gap-2">
    <span className="text-xs text-gray-500 w-28 shrink-0">{label}</span>
    <span className="text-sm text-gray-800">{value || <span className="text-gray-300">—</span>}</span>
  </div>
);

// ── Portal Tab ────────────────────────────────────────────────────────────────

const PortalTab: React.FC<{ customer: B2BCustomer; orgAlias: string; onUpdated: (c: B2BCustomer) => void }> = ({
  customer, orgAlias, onUpdated,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [error, setError] = useState('');

  const portalEnabled = !!customer.portal_enabled;
  const portalUrl = `${window.location.origin}/portal/${orgAlias}`;

  const run = async (fn: () => Promise<any>) => {
    setError('');
    setTempPassword(null);
    setLoading(true);
    try {
      const result = await fn();
      if (result?.temp_password) setTempPassword(result.temp_password);
      const refreshed = await b2bCustomerService.get(customer.id);
      onUpdated(refreshed.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('customerDrawer.portal.actionFailed'));
    } finally {
      setLoading(false);
    }
  };

  const showLink = portalEnabled;

  return (
    <div className="space-y-5">
      {/* Status */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
        <div>
          <p className="text-sm font-medium text-gray-800">{t('customerDrawer.portal.statusTitle')}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {customer.contact_email
              ? <>{t('customerDrawer.portal.loginEmail')} <span className="font-mono text-gray-700">{customer.contact_email}</span></>
              : <span className="text-orange-500">{t('customerDrawer.portal.noEmail')}</span>}
          </p>
          {customer.portal_last_login_at && (
            <p className="text-xs text-gray-400 mt-0.5">
              {t('customerDrawer.portal.lastLogin')} {new Date(customer.portal_last_login_at as string).toLocaleString()}
            </p>
          )}
        </div>
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
          (portalEnabled) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {(portalEnabled) ? t('customerDrawer.portal.enabled') : t('customerDrawer.portal.disabled')}
        </span>
      </div>

      {/* Portal link — shown when enabled */}
      {showLink && orgAlias && (
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          <span className="text-xs text-gray-500 shrink-0">{t('customerDrawer.portal.portalUrl')}</span>
          <span className="text-xs font-mono text-gray-700 flex-1 truncate">{portalUrl}</span>
          <button
            onClick={() => navigator.clipboard.writeText(portalUrl)}
            className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
            title={t('customerDrawer.portal.copyLink')}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
          <a
            href={portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1 text-blue-500 hover:text-blue-700 transition-colors"
            title={t('customerDrawer.portal.openPortal')}
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      )}

      {/* Temp password reveal */}
      {tempPassword && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-yellow-800">{t('customerDrawer.portal.tempPasswordTitle')}</p>
            <p className="text-sm text-yellow-700 mt-1">
              {t('customerDrawer.portal.tempPasswordShare')}{' '}
              <code className="font-mono bg-yellow-100 border border-yellow-300 px-2 py-0.5 rounded text-yellow-900">
                {tempPassword}
              </code>
            </p>
            <p className="text-xs text-yellow-600 mt-1">{t('customerDrawer.portal.tempPasswordHint')}</p>
          </div>
          <div className="flex gap-1 shrink-0">
            <button
              onClick={() => copyToClipboard(tempPassword)}
              className="p-1.5 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100 rounded-md transition-colors"
              title={t('customerDrawer.portal.copy')}
            >
              <Copy className="w-4 h-4" />
            </button>
            <button onClick={() => setTempPassword(null)} className="p-1.5 text-yellow-500 hover:text-yellow-700 rounded-md transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

      {/* Actions */}
      <div className="space-y-2">
        {!portalEnabled ? (
          <button
            onClick={() => run(() => enablePortalAccess(customer.id, {}))}
            disabled={loading || !customer.contact_email}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2.5 rounded-xl disabled:opacity-50 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
            {t('customerDrawer.portal.enableAccess')}
          </button>
        ) : (
          <>
            <button
              onClick={() => run(() => resetPortalPassword(customer.id))}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2.5 rounded-xl disabled:opacity-50 transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {t('customerDrawer.portal.resetPassword')}
            </button>
            <button
              onClick={() => run(() => disablePortalAccess(customer.id))}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 border border-red-200 hover:bg-red-50 text-red-600 text-sm font-medium px-4 py-2.5 rounded-xl disabled:opacity-50 transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              {t('customerDrawer.portal.disableAccess')}
            </button>
          </>
        )}
      </div>

      <div className="border-t border-gray-100 pt-4 text-xs text-gray-400 space-y-1">
        <p>• {t('customerDrawer.portal.hintUrl')} <span className="font-mono text-gray-600">/portal/{'<org_alias>'}</span></p>
        <p>• {t('customerDrawer.portal.hintLogin')}</p>
        <p>• {t('customerDrawer.portal.hintChange')}</p>
      </div>
    </div>
  );
};

// ── Tokens Tab ────────────────────────────────────────────────────────────────

const TokensTab: React.FC<{ customer: B2BCustomer }> = ({ customer }) => {
  const { t } = useTranslation();
  const [tokens, setTokens] = useState<B2BApiToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTokenName, setNewTokenName] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<B2BApiTokenCreated | null>(null);
  const [revoking, setRevoking] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await b2bCustomerService.listTokens(customer.id);
      setTokens(res.data.tokens);
    } catch {
      setError(t('customerDrawer.tokens.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [customer.id, t]);

  useEffect(() => { load(); }, [load]);

  const createToken = async () => {
    if (!newTokenName.trim()) return;
    setCreating(true);
    setError('');
    setCreated(null);
    try {
      const res = await b2bCustomerService.createToken(customer.id, { name: newTokenName.trim() });
      setCreated(res.data);
      setNewTokenName('');
      setShowCreateForm(false);
      setShowSecret(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('customerDrawer.tokens.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const revokeToken = async (tokenId: number) => {
    setRevoking(tokenId);
    try {
      await b2bCustomerService.revokeToken(customer.id, tokenId);
      setTokens(prev => prev.filter(t => t.id !== tokenId));
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('customerDrawer.tokens.revokeFailed'));
    } finally {
      setRevoking(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">{t('customerDrawer.tokens.description')}</p>
        <button
          onClick={() => { setShowCreateForm(v => !v); setCreated(null); setError(''); }}
          className="flex items-center gap-1.5 text-sm bg-gray-900 text-white px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> {t('customerDrawer.tokens.newToken')}
        </button>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-gray-700">{t('customerDrawer.tokens.createTitle')}</p>
          <div className="flex gap-2">
            <input
              className={INPUT + ' flex-1'}
              placeholder={t('customerDrawer.tokens.createPlaceholder')}
              value={newTokenName}
              onChange={e => setNewTokenName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createToken()}
            />
            <button
              onClick={createToken}
              disabled={creating || !newTokenName.trim()}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : t('customerDrawer.tokens.create')}
            </button>
          </div>
          <p className="text-xs text-gray-400">{t('customerDrawer.tokens.scopesHint')}</p>
        </div>
      )}

      {/* Newly created secret — show once */}
      {created && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-green-800">{t('customerDrawer.tokens.createdTitle')}</p>
          <div>
            <p className="text-xs text-green-700 mb-1 font-medium">{t('customerDrawer.tokens.clientId')}</p>
            <div className="flex items-center gap-2">
              <code className="text-xs font-mono bg-white border border-green-200 px-2 py-1 rounded flex-1 break-all">{created.client_id}</code>
              <button onClick={() => copyToClipboard(created.client_id)} className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <div>
            <p className="text-xs text-green-700 mb-1 font-medium">{t('customerDrawer.tokens.clientSecret')} <span className="text-green-500">{t('customerDrawer.tokens.shownOnce')}</span></p>
            <div className="flex items-center gap-2">
              <code className="text-xs font-mono bg-white border border-green-200 px-2 py-1 rounded flex-1 break-all">
                {showSecret ? created.client_secret : '•'.repeat(40)}
              </code>
              <button onClick={() => setShowSecret(v => !v)} className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors">
                {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => copyToClipboard(created.client_secret)} className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <p className="text-xs text-green-600">{created.message}</p>
          <button onClick={() => setCreated(null)} className="text-xs text-green-600 hover:text-green-800 underline">{t('customerDrawer.tokens.dismiss')}</button>
        </div>
      )}

      {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

      {/* Token list */}
      {loading ? (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('customerDrawer.tokens.loading')}
        </div>
      ) : tokens.length === 0 ? (
        <div className="text-center py-10">
          <KeyRound className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">{t('customerDrawer.tokens.empty')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tokens.map(tok => (
            <div key={tok.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{tok.name}</p>
                <p className="text-xs text-gray-400 font-mono truncate">{tok.client_id}</p>
                <p className="text-xs text-gray-400">
                  {t('customerDrawer.tokens.created')} {new Date(tok.created_at).toLocaleDateString()}
                  {tok.last_used_at && <> · {t('customerDrawer.tokens.lastUsed')} {new Date(tok.last_used_at).toLocaleDateString()}</>}
                </p>
              </div>
              <button
                onClick={() => revokeToken(tok.id)}
                disabled={revoking === tok.id}
                className="ml-3 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                title={t('customerDrawer.tokens.revokeTitle')}
              >
                {revoking === tok.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-gray-100 pt-4 text-xs text-gray-400 space-y-1">
        <p>• {t('customerDrawer.tokens.hintEndpoint')} <code className="font-mono">POST /api/v1/oauth/token</code></p>
        <p>• {t('customerDrawer.tokens.hintGrant')}</p>
        <p>• {t('customerDrawer.tokens.hintOrders')} <code className="font-mono">GET /api/v1/b2b/orders</code>.</p>
      </div>
    </div>
  );
};

// ── Main Drawer ───────────────────────────────────────────────────────────────

const CustomerDrawer: React.FC<Props> = ({ customer, orgAlias, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('info');
  const [localCustomer, setLocalCustomer] = useState<B2BCustomer | null>(customer);

  const TABS: { id: Tab; label: string; Icon: React.FC<{ className?: string }> }[] = [
    { id: 'info', label: t('customerDrawer.tab.info'), Icon: Building2 },
    { id: 'portal', label: t('customerDrawer.tab.portal'), Icon: Globe },
    { id: 'tokens', label: t('customerDrawer.tab.tokens'), Icon: KeyRound },
  ];

  useEffect(() => {
    setLocalCustomer(customer);
    setTab('info');
  }, [customer?.id]);

  const handleUpdated = (c: B2BCustomer) => {
    setLocalCustomer(c);
    onUpdated(c);
  };

  if (!localCustomer) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 z-50 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 truncate">{localCustomer.name}</h2>
            {localCustomer.alias && <p className="text-xs text-gray-400 truncate">{localCustomer.alias}</p>}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 px-6">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === id
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {tab === 'info' && <InfoTab customer={localCustomer} onUpdated={handleUpdated} />}
          {tab === 'portal' && <PortalTab customer={localCustomer} orgAlias={orgAlias} onUpdated={handleUpdated} />}
          {tab === 'tokens' && <TokensTab customer={localCustomer} />}
        </div>
      </div>
    </>
  );
};

export default CustomerDrawer;
